import React, { useState } from "react";
import { Form, Input, Button, Typography } from "antd";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import authService from "../services/authService";
import { useAuth } from "../contexts/AuthContext";

const { Title } = Typography;

const LoginForm = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const res = await authService.loginUser(values);

      const { user, token } = res;

     
      toast.success("Login successful!");

      login(user, token);

      if (user?.role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/user/dashboard");
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Network error or server not responding";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* --- Left Side --- */}
      <div className="hidden md:flex md:w-1/2 bg-[#1e5782] text-white items-center justify-center flex-col p-10">
        <h1 className="text-3xl font-semibold mb-4 text-center leading-snug">
          Welcome Back to Eye on Health
        </h1>
        <p className="text-gray-200 text-center max-w-md text-sm leading-relaxed">
          Login to help customers create a plan customized to their needs.
        </p>
        <div className="mt-8 w-16 h-[2px] bg-white/30 rounded-full"></div>
      </div>

      {/* --- Right Side (Form) --- */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-white px-8 py-16">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="md:hidden flex justify-center mb-8">
            <img
              src="/assets/logo.png"
              alt="Company Logo"
              className="w-36 h-12 object-contain"
            />
          </div>

          <Title level={3} className="text-center mb-8 text-gray-800">
            Sign in to your account
          </Title>

          <Form
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            className="space-y-4"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Enter your email"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
                size="large"
              />
            </Form.Item>

            <div className="flex items-center justify-between mb-4 text-sm">
              <Link
                to="/register"
                className="text-[#1e5782] font-medium hover:underline"
              >
                Create new account
              </Link>
              <Link
                to="/forgot-password"
                className="text-[#1e5782] font-medium hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="w-full bg-[#1e5782] hover:bg-[#184766] flex items-center justify-center mt-4"
                size="large"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex space-x-1">
                    <span className="dot w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <span className="dot w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <span className="dot w-2 h-2 bg-white rounded-full animate-bounce" />
                  </span>
                ) : (
                  "Login"
                )}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
